import { apiClient } from './apiClient';
import { ACCOUNTS_API_PATHS } from './accountsApiPaths';
import { CreateUserRequest, UpdateUserRequest, User } from '../types/users.types';
import { CreateRoleRequest, Role, UpdateRoleRequest } from '../types/roles.types';
import { LoginHistoryRecord, LoginRequest, PasswordHistoryRecord, ResetPasswordPayload, sendTemporaryPasswordEmailRequest } from '../types/auth.types';
import { ID_OBJ, MasterDataItem, MSG_OBJ } from '../types/common.types';
import { CrudRequestOptions, generateCrudApi } from '../utils/generateModuleCrud';
import { CreateExpenseRequest, Expense, UpdateExpenseRequest } from '../pages/expenses/expense.types';
import { CreateDocumentRequest, Document, UpdateDocumentRequest } from '../pages/documents/document.types';

interface UpdateDocumentWithFilesPayload {
  files?: File[];
  name?: string;
  type?: string;
  existingFileNames?: string[];
  removeFileNames?: string[];
}

//Sample API client usage
/* export const fetchUsers = () => apiClient<User[]>({ method: 'get', url: ACCOUNTS_API_PATHS.users });
export const getUser = (id: string) => apiClient<User>({ method: 'get', url: ACCOUNTS_API_PATHS.userId(id) });
export const createUser = (data: CreateUserRequest) => apiClient<User>({ method: 'post', url: ACCOUNTS_API_PATHS.users, data });
export const updateUser = (id: string, data: UpdateUserRequest) =>
  apiClient<User>({ method: 'patch', url: ACCOUNTS_API_PATHS.userId(id), data });
export const deleteUser = (id: string) => apiClient<User>({ method: 'delete', url: ACCOUNTS_API_PATHS.userId(id) }); */

// 👤 Users 
export const userApi = generateCrudApi<User, CreateUserRequest, UpdateUserRequest>({
  basePath: ACCOUNTS_API_PATHS.users,
  buildIdPath: (id) => ACCOUNTS_API_PATHS.userId(id),
  batchCreatePath:ACCOUNTS_API_PATHS.usersBatchCreate
}); 

// 🛡️ Roles 
export const roleApi = generateCrudApi<Role, CreateRoleRequest, UpdateRoleRequest>({
  basePath: ACCOUNTS_API_PATHS.roles,
  buildIdPath: (id) => ACCOUNTS_API_PATHS.roleId(id),
  batchCreatePath:ACCOUNTS_API_PATHS.rolesBatchCreate
}); 


// 📚 Masters  
export const fetchUsersMasterData = () => apiClient<MasterDataItem[]>({ method: 'get', url: ACCOUNTS_API_PATHS.usersMasterData });
export const fetchRolesMasterData = () => apiClient<MasterDataItem[]>({ method: 'get', url: ACCOUNTS_API_PATHS.rolesMasterData });



// Auth
export const loginUser = (credentials: LoginRequest) => apiClient<User>({ method: 'post', url: ACCOUNTS_API_PATHS.userLogin, data: credentials });
export const refreshToken = () => apiClient<MSG_OBJ>({ method: 'get', url: ACCOUNTS_API_PATHS.refreshToken });
export const resetPassword = (payload: ResetPasswordPayload) =>
  apiClient<MSG_OBJ>({ method: 'post', url: ACCOUNTS_API_PATHS.resetPassword, data: payload });
export const sendTemporaryPasswordEmail = (credentials: sendTemporaryPasswordEmailRequest) =>
  apiClient<MSG_OBJ>({ method: 'post', url: ACCOUNTS_API_PATHS.sendTemporaryPasswordEmail, data: credentials });


//History
export const loginHistoryByUserId = (params: ID_OBJ)=>
  apiClient<LoginHistoryRecord[]>({ method: 'get', url: ACCOUNTS_API_PATHS.loginHistoryByUserId(params.id) });
export const passwordHistoryByUserId = (params:ID_OBJ)=>
  apiClient<PasswordHistoryRecord[]>({ method: 'get', url: ACCOUNTS_API_PATHS.passwordHistoryByUserId(params.id) });

// 💰 Expenses  
export const expenseApi = generateCrudApi<Expense, CreateExpenseRequest, UpdateExpenseRequest>({
  basePath: ACCOUNTS_API_PATHS.expenses,
  buildIdPath: (id) => ACCOUNTS_API_PATHS.expensesId(id),
  batchCreatePath: ACCOUNTS_API_PATHS.expensesBatchCreate
});

// 📄 Documents
const documentCrudApi = generateCrudApi<Document, CreateDocumentRequest, UpdateDocumentRequest>({
  basePath: ACCOUNTS_API_PATHS.documents,
  buildIdPath: (id) => ACCOUNTS_API_PATHS.documentsId(id),
});
export const documentApi = {
  ...documentCrudApi,
  create: (formData: FormData, options?: CrudRequestOptions) =>
    apiClient<Document>({
      method: 'post',
      url: ACCOUNTS_API_PATHS.documents,
      data: formData,
      isFormData: true,
      ...(options || {}),
    }),
  updateWithFiles: (id: string, payload: UpdateDocumentWithFilesPayload, options?: CrudRequestOptions) => {
    const formData = new FormData();
    (payload.files || []).forEach((file) => formData.append('files', file));
    if (typeof payload.name === 'string') formData.append('name', payload.name);
    if (typeof payload.type === 'string') formData.append('type', payload.type);
    (payload.existingFileNames || []).forEach((fileName) => formData.append('existingFileNames', fileName));
    (payload.removeFileNames || []).forEach((fileName) => formData.append('removeFileNames', fileName));
    return apiClient<Document>({
      method: 'patch',
      url: ACCOUNTS_API_PATHS.documentsId(id),
      data: formData,
      isFormData: true,
      ...(options || {}),
    });
  },
};

// 📚 Expense Category Combinations
export const fetchExpenseCategoryCombinations = () => 
  apiClient<{ success: boolean; data: string[]; count: number }>({ 
    method: 'get', 
    url: ACCOUNTS_API_PATHS.expensesCategoryCombinations 
  });
